/** EFFET : implémentation PeerJS de `Connector` / `PeerLink`. */
import Peer, { type DataConnection, type MediaConnection, type PeerOptions } from 'peerjs';
import { encodeMessage, parseMessage, type Message } from '../game/protocol';
import type { IceConfig } from './iceConfig';
import { connectionKind } from './connectionKind';
import { mergeIceServers } from './turn';
import { createReconnector, type Reconnector } from './reconnector';
import { createBuffered, createLatest } from '../lib/channels';
import { ConnectionError, type Connector, type HostSession, type PeerLink } from './signaling';

export interface BrokerEnv {
  readonly VITE_PEERJS_HOST?: string;
  readonly VITE_PEERJS_PORT?: string;
  readonly VITE_PEERJS_PATH?: string;
  readonly VITE_PEERJS_SECURE?: string;
}

const JOIN_TIMEOUT_MS = 20_000;
const GAME = 'game';
const MESH = 'mesh';

function peerOptions(env: BrokerEnv, iceServers: RTCIceServer[]): PeerOptions {
  const options: PeerOptions = { config: { iceServers } };
  if (env.VITE_PEERJS_HOST) {
    options.host = env.VITE_PEERJS_HOST;
    options.port = Number(env.VITE_PEERJS_PORT ?? 443);
    options.path = env.VITE_PEERJS_PATH ?? '/';
    options.secure = env.VITE_PEERJS_SECURE !== 'false';
  }
  return options;
}

function toConnectionError(e: unknown): ConnectionError {
  const type = (e as { type?: string } | null)?.type;
  switch (type) {
    case 'peer-unavailable':
      return new ConnectionError(
        'roomNotFound',
        'Partie introuvable : ton ami a fermé sa page, ou sa connexion au serveur est coupée. Demande-lui de garder la page ouverte (au premier plan), puis réessaie.',
        { cause: e },
      );
    case 'unavailable-id':
      return new ConnectionError('roomTaken', 'Identifiant de partie déjà utilisé.', { cause: e });
    case 'browser-incompatible':
      return new ConnectionError('unsupported', 'Ce navigateur ne gère pas WebRTC.', { cause: e });
    default:
      return new ConnectionError('brokerUnreachable', 'Serveur de mise en relation injoignable.', {
        cause: e,
      });
  }
}

function waitOpen(peer: Peer): Promise<string> {
  return new Promise((resolve, reject) => {
    peer.once('open', resolve);
    peer.once('error', (e) => {
      reject(toConnectionError(e));
    });
  });
}

function waitConnOpen(conn: DataConnection): Promise<DataConnection> {
  return new Promise((resolve, reject) => {
    if (conn.open) resolve(conn);
    conn.once('open', () => {
      resolve(conn);
    });
    conn.once('error', reject);
  });
}

function withTimeout<T>(promise: Promise<T>, ms: number, onTimeout: () => void): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      onTimeout();
      reject(
        new ConnectionError(
          'timeout',
          'Connexion impossible avec ton ami. Un réseau (entreprise, 4G) bloque peut-être la connexion directe : il faudrait un serveur TURN.',
        ),
      );
    }, ms);
    promise.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e: unknown) => {
        clearTimeout(timer);
        reject(e instanceof Error ? e : new Error(String(e)));
      },
    );
  });
}

function createLink(
  peer: Peer,
  remoteId: string,
  game: DataConnection,
  mesh: DataConnection,
): PeerLink {
  // Gardés jusqu'à l'abonnement de l'écran de jeu (caméra lente à s'ouvrir) :
  // sinon le `hello` de l'hôte — et donc le mode de jeu — serait perdu.
  const messages = createBuffered<Message>();
  const meshListeners = new Set<(d: ArrayBuffer) => void>();
  // Dernier flux reçu, rejoué à l'écran qui s'abonne après son arrivée.
  const remoteStream = createLatest<MediaStream | null>(null);
  const closeListeners = new Set<() => void>();
  let outgoingCall: MediaConnection | null = null;
  let closed = false;
  let rejected = 0;

  game.on('data', (raw) => {
    const r = parseMessage(raw);
    if (r.ok) messages.push(r.value);
    else if (++rejected <= 5) console.warn('message rejeté :', r.error);
  });
  mesh.on('data', (raw) => {
    if (raw instanceof ArrayBuffer) for (const l of meshListeners) l(raw);
    else if (ArrayBuffer.isView(raw)) {
      const copy = new Uint8Array(raw.byteLength);
      copy.set(new Uint8Array(raw.buffer, raw.byteOffset, raw.byteLength));
      for (const l of meshListeners) l(copy.buffer);
    }
  });

  // Flux entrant : le pair nous appelle avec SON flux, on répond sans rien envoyer.
  // Quand il change de flux (visio ↔ maillage), le nouvel appel peut arriver avant
  // la fermeture de l'ancien : seule la fermeture de l'appel COURANT compte.
  let incoming: MediaConnection | null = null;
  peer.on('call', (call) => {
    if (call.peer !== remoteId) {
      call.close();
      return;
    }
    incoming = call;
    call.answer();
    call.on('stream', (stream) => {
      if (call === incoming) remoteStream.set(stream);
    });
    call.on('close', () => {
      if (call !== incoming) return;
      incoming = null;
      remoteStream.set(null);
    });
  });

  const close = (): void => {
    if (closed) return;
    closed = true;
    outgoingCall?.close();
    game.close();
    mesh.close();
    peer.destroy();
    for (const l of closeListeners) l();
  };
  game.on('close', close);

  const subscribe =
    <T>(set: Set<T>) =>
    (listener: T): (() => void) => {
      set.add(listener);
      return () => set.delete(listener);
    };

  return {
    send(message) {
      if (game.open) void game.send(encodeMessage(message));
    },
    sendMesh(data) {
      if (mesh.open) void mesh.send(data);
    },
    setOutgoingStream(stream) {
      outgoingCall?.close();
      outgoingCall = stream ? peer.call(remoteId, stream) : null;
    },
    onMessage: (listener) => messages.subscribe(listener),
    connectionKind: async () => {
      const pc = (game as { peerConnection?: RTCPeerConnection }).peerConnection;
      if (!pc) return null;
      const stats = await pc.getStats();
      return connectionKind([...stats.values()] as { id: string; type: string }[]);
    },
    onMesh: subscribe(meshListeners),
    onRemoteStream: (listener) => remoteStream.subscribe(listener),
    onClose: subscribe(closeListeners),
    isOpen: () => !closed && game.open,
    close,
  };
}

const SIGNALING_ERRORS = new Set(['network', 'server-error', 'socket-error', 'socket-closed']);

/**
 * Garde la connexion au serveur de mise en relation : PeerJS ne se reconnecte
 * pas tout seul (onglet en arrière-plan, réseau qui change…). Même identifiant :
 * le lien d'invitation reste valable.
 */
function keepSignalingAlive(peer: Peer): Reconnector {
  const reconnector = createReconnector({
    reconnect: () => {
      if (!peer.destroyed && peer.disconnected) peer.reconnect();
    },
    schedule: (ms, fn) => {
      const id = setTimeout(fn, ms);
      return () => {
        clearTimeout(id);
      };
    },
  });
  const onVisibility = (): void => {
    if (document.visibilityState === 'visible') reconnector.onVisible();
  };
  peer.on('disconnected', () => {
    if (!peer.destroyed) reconnector.onDisconnected();
  });
  peer.on('open', () => {
    reconnector.onOpen();
  });
  peer.on('error', (e) => {
    if (!peer.destroyed && peer.disconnected && SIGNALING_ERRORS.has(e.type))
      reconnector.onDisconnected();
  });
  peer.on('close', () => {
    reconnector.stop();
    document.removeEventListener('visibilitychange', onVisibility);
  });
  document.addEventListener('visibilitychange', onVisibility);
  return reconnector;
}

/** Build e2e uniquement : simuler la perte du serveur de mise en relation. */
function exposeTestHooks(peer: Peer): void {
  if (import.meta.env['VITE_E2E'] !== 'true') return;
  const w = window as unknown as { __barbichetteTest?: Record<string, unknown> };
  w.__barbichetteTest = {
    ...w.__barbichetteTest,
    disconnectSignaling: () => {
      peer.disconnect();
    },
  };
}

/**
 * `extraIce` : serveurs obtenus au moment de jouer (relais TURN Cloudflare, identifiants
 * de courte durée) ; ajoutés aux STUN/TURN de la configuration du build.
 */
export function createPeerConnector(
  env: BrokerEnv,
  ice: IceConfig,
  extraIce: () => Promise<RTCIceServer[]> = () => Promise.resolve([]),
): Connector {
  const options = async (): Promise<PeerOptions> =>
    peerOptions(env, mergeIceServers(ice.iceServers, await extraIce()));
  return {
    async host(roomId): Promise<HostSession> {
      const peer = new Peer(roomId, await options());
      await waitOpen(peer);
      const signaling = keepSignalingAlive(peer);
      exposeTestHooks(peer);
      let taken = false;
      const pending = new Map<string, Partial<Record<string, DataConnection>>>();
      const guest = new Promise<PeerLink>((resolve) => {
        peer.on('connection', (conn) => {
          if (taken) {
            conn.close(); // une partie = deux joueurs
            return;
          }
          const slot = pending.get(conn.peer) ?? {};
          slot[conn.label] = conn;
          pending.set(conn.peer, slot);
          const { [GAME]: game, [MESH]: mesh } = slot;
          if (!game || !mesh) return;
          taken = true;
          void Promise.all([waitConnOpen(game), waitConnOpen(mesh)]).then(() => {
            resolve(createLink(peer, conn.peer, game, mesh));
          });
        });
      });
      return {
        roomId,
        signaling,
        waitForGuest: () => guest,
        close: () => {
          peer.destroy();
        },
      };
    },

    async join(roomId): Promise<PeerLink> {
      const peer = new Peer(await options());
      const connect = async (): Promise<PeerLink> => {
        await waitOpen(peer);
        const failed = new Promise<never>((_, reject) => {
          peer.once('error', (e) => {
            reject(toConnectionError(e));
          });
        });
        const game = peer.connect(roomId, { label: GAME, serialization: 'raw', reliable: true });
        const mesh = peer.connect(roomId, { label: MESH, serialization: 'raw', reliable: false });
        await Promise.race([Promise.all([waitConnOpen(game), waitConnOpen(mesh)]), failed]);
        // Les changements de flux (visio ↔ maillage) passent encore par le serveur.
        keepSignalingAlive(peer);
        return createLink(peer, roomId, game, mesh);
      };
      return withTimeout(connect(), JOIN_TIMEOUT_MS, () => {
        peer.destroy();
      });
    },
  };
}
